import {
  CREDENTIAL_MASK,
  type CredentialDto,
  VAULT_MESSAGES,
  VAULT_REVEAL_VISIBLE_SEC,
  type VaultRevealPurpose,
} from '@fos/shared/contracts/vault';
import { todayLocal } from '@fos/shared/time';
import { Copy, ExternalLink, Eye, EyeOff, KeyRound, Lock } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { appErrorCode, errorMessage } from '../../../../lib/api';
import { trpc } from '../../../../lib/trpc';
import { IconButton } from '../../../../ui/Button';
import { Section } from '../../../../ui/Section';
import { EmptyState, ErrorState, Skeleton } from '../../../../ui/States';
import { toast } from '../../../../ui/Toast';
import { Tooltip } from '../../../../ui/Tooltip';
import { CardSectionHeader, SectionAction } from '../MaintenanceBits';
import { CredentialFormDialog } from './CredentialFormDialog';
import { RevealDialog } from './RevealDialog';
import { copyToClipboard, dateText, safeHref } from './vault-utils';

/** Jawny sekret po podglądzie: tylko w stanie tego komponentu, czyszczony po 30 s, przy ukryciu i odmontowaniu. */
interface Revealed {
  id: string;
  secret: string;
  until: number;
}

type DialogState =
  | { kind: 'reveal'; credential: CredentialDto; purpose: VaultRevealPurpose }
  | { kind: 'form'; credential?: CredentialDto }
  | null;

/**
 * Sekcja „Dostępy” w karcie zasobu (Figma 08 „Zasób”, DESIGN §5 „Sejf dostępów”): wiersz z ikoną klucza, nazwa,
 * login (mono), hasło jako STAŁA liczba kropek na bg/surface, ikony „pokaż” i „kopiuj”. Podgląd i kopia po
 * potwierdzeniu kodem z maila (Fala 7: najpierw próba odczytu — w 5 min po kodzie bez okna; inaczej `RevealDialog`
 * „Wyślij kod” → kod), sekret widoczny 30 s z odliczaniem i automatycznym
 * ukryciem (stan czyszczony, nie tylko CSS; także po przejściu karty w tło). Kopiowanie sekretu już widocznego:
 * schowek + `vault.logCopy`; w przeciwnym razie `vault.reveal` z `purpose: 'copy'` (sekret nie pojawia się na ekranie).
 * Osoba bez sejfu zasobu: „Dostępy: ograniczone” — bez przycisków i bez liczby dostępów.
 */
export function CredentialsSection({ assetId, assetName }: { assetId: string; assetName: string }) {
  const query = trpc.vault.list.useQuery({ assetId });
  const utils = trpc.useUtils();
  const logCopy = trpc.vault.logCopy.useMutation();
  const [dialog, setDialog] = useState<DialogState>(null);
  const [revealed, setRevealed] = useState<Revealed | null>(null);
  const [now, setNow] = useState(() => Date.now());

  // odliczanie i automatyczne ukrycie; karta w tle = ukrycie od razu
  useEffect(() => {
    if (!revealed) return;
    const tick = () => {
      const t = Date.now();
      if (t >= revealed.until || document.visibilityState === 'hidden') setRevealed(null);
      else setNow(t);
    };
    tick();
    const interval = window.setInterval(tick, 250);
    document.addEventListener('visibilitychange', tick);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [revealed]);

  // inny zasób albo odmontowanie: żadnego sekretu w stanie
  // biome-ignore lint/correctness/useExhaustiveDependencies: czyszczenie przy zmianie zasobu
  useEffect(() => () => setRevealed(null), [assetId]);

  const performReveal = useCallback(
    async (credential: CredentialDto, purpose: VaultRevealPurpose) => {
      // własne okno potwierdzenia (RevealDialog), nie globalne: STEP_UP_REQUIRED wraca tutaj
      const res = await utils.client.vault.reveal.mutate(
        { id: credential.id, purpose },
        { context: { skipStepUp: true } },
      );
      if (purpose === 'copy') {
        const ok = await copyToClipboard(res.secret);
        if (ok) toast.success(`Skopiowano hasło „${credential.label}”`);
        else {
          // przeglądarka nie pozwoliła na schowek: pokazujemy sekret na 30 s (podgląd i tak jest w audycie)
          setRevealed({ id: credential.id, secret: res.secret, until: Date.now() + VAULT_REVEAL_VISIBLE_SEC * 1000 });
          toast.message('Nie udało się skopiować. Hasło widać przez 30 sekund.');
        }
      } else {
        setRevealed({ id: credential.id, secret: res.secret, until: Date.now() + VAULT_REVEAL_VISIBLE_SEC * 1000 });
      }
      void utils.vault.list.invalidate({ assetId });
    },
    [utils, assetId],
  );

  /** Podgląd albo kopia: w 5 min po kodzie z maila od razu, inaczej okno „Potwierdź, że to Ty”. */
  const requestReveal = async (credential: CredentialDto, purpose: VaultRevealPurpose) => {
    try {
      await performReveal(credential, purpose);
    } catch (err) {
      if (appErrorCode(err) === 'STEP_UP_REQUIRED') setDialog({ kind: 'reveal', credential, purpose });
      else toast.error(errorMessage(err));
    }
  };

  const copyVisible = async (credential: CredentialDto, secret: string) => {
    if (!(await copyToClipboard(secret))) {
      toast.error('Przeglądarka nie pozwoliła skopiować hasła. Zaznacz je i skopiuj ręcznie.');
      return;
    }
    toast.success(`Skopiowano hasło „${credential.label}”`);
    logCopy.mutate({ id: credential.id });
  };

  const header = (action?: ReactNode) => <CardSectionHeader title="Dostępy" action={action} />;

  if (query.isPending) {
    return (
      <Section aria-label="Dostępy">
        {header()}
        <div role="status" aria-label="Wczytywanie dostępów">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex min-h-[46px] items-center gap-3 border-b border-line last:border-b-0">
              <Skeleton className="size-4 rounded" />
              <Skeleton className="h-3.5 w-28" />
              <span className="flex-1" />
              <Skeleton className="h-6 w-20" />
            </div>
          ))}
        </div>
      </Section>
    );
  }
  if (query.isError) {
    return (
      <Section aria-label="Dostępy">
        {header()}
        <ErrorState
          compact
          title="Nie udało się wczytać dostępów"
          onRetry={() => void query.refetch()}
          retrying={query.isFetching}
        />
      </Section>
    );
  }

  const { access, canManage, items } = query.data;
  if (access === 'restricted') {
    return (
      <Section aria-label="Dostępy">
        {header()}
        <div className="flex items-start gap-2.5 border-b border-line py-3">
          <Lock size={15} strokeWidth={1.75} aria-hidden className="mt-0.5 shrink-0 text-fg-muted" />
          <p className="text-body-s text-fg-secondary">
            {VAULT_MESSAGES.restricted}
            <span className="block text-caption text-fg-muted">{VAULT_MESSAGES.restrictedHint}</span>
          </p>
        </div>
      </Section>
    );
  }

  const add = canManage ? (
    <SectionAction onClick={() => setDialog({ kind: 'form' })}>Dodaj dostęp</SectionAction>
  ) : undefined;
  const secondsLeft = revealed ? Math.max(0, Math.ceil((revealed.until - now) / 1000)) : 0;

  return (
    <Section aria-label="Dostępy">
      {header(add)}
      {items.length === 0 ? (
        <EmptyState
          compact
          icon={KeyRound}
          title="Brak zapisanych dostępów"
          description="Panel hostingu, FTP, baza danych. Hasła zapisujemy zaszyfrowane."
        />
      ) : (
        // lista w białej sekcji: tylko linie między wierszami, bez własnej ramki (przegląd Fali 9: „ramka w ramce”)
        <ul aria-label="Lista dostępów" className="mt-1">
          {items.map((c) => (
            <CredentialRow
              key={c.id}
              credential={c}
              canManage={canManage}
              revealed={revealed?.id === c.id ? { secret: revealed.secret, secondsLeft } : null}
              onReveal={() => void requestReveal(c, 'reveal')}
              onHide={() => setRevealed(null)}
              onCopy={() => {
                if (revealed?.id === c.id) void copyVisible(c, revealed.secret);
                else void requestReveal(c, 'copy');
              }}
              onEdit={() => setDialog({ kind: 'form', credential: c })}
            />
          ))}
        </ul>
      )}
      <p className="mt-2 flex items-start gap-1.5 text-caption text-fg-muted">
        <Lock size={13} strokeWidth={1.75} aria-hidden className="mt-0.5 shrink-0" />
        {VAULT_MESSAGES.footer}
      </p>

      <RevealDialog
        open={dialog?.kind === 'reveal'}
        onOpenChange={(open) => !open && setDialog(null)}
        credential={dialog?.kind === 'reveal' ? dialog.credential : null}
        assetName={assetName}
        purpose={dialog?.kind === 'reveal' ? dialog.purpose : 'reveal'}
        onConfirmed={async () => {
          if (dialog?.kind === 'reveal') await performReveal(dialog.credential, dialog.purpose);
        }}
      />
      {canManage && (
        <CredentialFormDialog
          open={dialog?.kind === 'form'}
          onOpenChange={(open) => !open && setDialog(null)}
          assetId={assetId}
          credential={dialog?.kind === 'form' ? dialog.credential : undefined}
        />
      )}
    </Section>
  );
}

function CredentialRow({
  credential: c,
  canManage,
  revealed,
  onReveal,
  onHide,
  onCopy,
  onEdit,
}: {
  credential: CredentialDto;
  canManage: boolean;
  revealed: { secret: string; secondsLeft: number } | null;
  onReveal: () => void;
  onHide: () => void;
  onCopy: () => void;
  onEdit: () => void;
}) {
  const panel = safeHref(c.panelUrl);
  const external = safeHref(c.externalRef);
  return (
    // telefon (< 640 px): w pierwszej linii nazwa i przyciski, login w całości w drugiej (kolumna nazwy jest
    // `contents`, więc nazwa i login są osobnymi elementami wiersza); od 640 px jak dotąd — nazwa 150 px i login obok
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-line py-2.5 last:border-b-0 sm:min-h-[46px] sm:py-1.5">
      <KeyRound size={15} strokeWidth={1.75} aria-hidden className="shrink-0 text-fg-muted" />
      <div className="contents sm:flex sm:min-w-0 sm:flex-1 sm:flex-row sm:items-center sm:gap-4">
        <span className="flex min-w-0 flex-1 flex-col sm:w-[150px] sm:flex-none">
          {canManage ? (
            <button
              type="button"
              onClick={onEdit}
              aria-label={`Edytuj dostęp ${c.label}`}
              className="min-w-0 truncate rounded-xs text-left text-body-s font-medium text-fg hover:underline focus-visible:outline-2 focus-visible:outline-accent"
            >
              {c.label}
            </button>
          ) : (
            <span className="min-w-0 truncate text-body-s font-medium text-fg">{c.label}</span>
          )}
          {c.rotationDue && (
            <span className="text-caption text-warning">
              {c.rotationDue.dueOn < todayLocal()
                ? `Zmień hasło (po terminie ${dateText(c.rotationDue.dueOn)})`
                : `Zmień hasło do ${dateText(c.rotationDue.dueOn)}`}
            </span>
          )}
        </span>
        {c.login && (
          <span className="min-w-0 truncate font-mono text-[13px] text-fg-secondary max-sm:order-last max-sm:basis-full max-sm:pl-[27px]">
            {c.login}
          </span>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-1">
        {panel && (
          <Tooltip content="Otwórz panel">
            <a
              href={panel}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Otwórz panel ${c.label}`}
              className="inline-flex size-7 items-center justify-center rounded-md text-fg-secondary hover:bg-elevated hover:text-fg focus-visible:outline-2 focus-visible:outline-accent"
            >
              <ExternalLink size={15} strokeWidth={1.75} aria-hidden />
            </a>
          </Tooltip>
        )}
        {c.hasSecret ? (
          <>
            {!revealed && (
              <span
                role="img"
                aria-label="Hasło ukryte"
                className="mr-1 inline-flex h-7 w-[62px] items-center justify-center rounded-md bg-surface font-mono text-[11px] tracking-[0.02em] text-fg-secondary sm:w-[92px] sm:text-[12px] sm:tracking-[0.1em]"
              >
                {CREDENTIAL_MASK}
              </span>
            )}
            {c.canReveal && (
              <>
                <IconButton
                  icon={revealed ? EyeOff : Eye}
                  label={revealed ? `Ukryj hasło ${c.label}` : `Pokaż hasło ${c.label}`}
                  onClick={revealed ? onHide : onReveal}
                />
                <IconButton icon={Copy} label={`Kopiuj hasło ${c.label}`} onClick={onCopy} />
              </>
            )}
          </>
        ) : external ? (
          <a
            href={external}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 rounded-xs text-caption text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent"
          >
            Menedżer haseł
            <ExternalLink size={12} strokeWidth={1.75} aria-hidden />
          </a>
        ) : (
          <span className="text-caption text-fg-muted">{c.externalRef ? 'w menedżerze haseł' : 'bez hasła'}</span>
        )}
      </div>

      {revealed && (
        <div className="flex w-full items-center gap-2 pb-1 max-sm:order-last sm:pl-[27px]">
          <span className="min-w-0 flex-1 rounded-md bg-surface px-2.5 py-1.5 font-mono text-[13px] break-all text-fg select-all">
            {revealed.secret}
          </span>
          <span
            role="timer"
            className="shrink-0 text-caption text-fg-muted"
            aria-label={`Ukryjemy za ${revealed.secondsLeft} s`}
          >
            {revealed.secondsLeft} s
          </span>
        </div>
      )}
    </li>
  );
}
